// 출석 현황 페이지 (백엔드 API 사용) - 타임라인 형태

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';
import { API_BASE_URL } from '../../../config/api';

// 한 번에 보여줄 기록 수 (2주 = 14일)
const INITIAL_DISPLAY_COUNT = 14;

// 자녀 타입
interface Child {
  id: string;
  name: string;
  organizationName: string;
}

// 개별 출입 기록 타입
interface AccessLog {
  id: string;
  timestamp: string;
  action: string;
  outing_type?: string;
}

// 날짜별 출석 기록 타입
interface DailyRecord {
  date: string;
  logs: AccessLog[];
  firstEntry?: string;
  lastExit?: string;
  hasOuting: boolean;
  isLoading?: boolean;  // 상세 로그 로딩 중
  status?: string;  // 정상, 지각, 조퇴, 지각/조퇴, 결석, 휴무
  expectedCheckin?: string;  // 예상 입실 시간
  expectedCheckout?: string; // 예상 퇴실 시간
}

export default function AttendancePage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { parent } = useSupabaseParentAuth();

  const [children, setChildren] = useState<Child[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string>(childId || '');
  const [dailyRecords, setDailyRecords] = useState<DailyRecord[]>([]);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);

  // 오늘 날짜
  const today = new Date();

  useEffect(() => {
    if (parent?.id) {
      loadChildren();
    }
  }, [parent?.id]);

  useEffect(() => {
    if (selectedChildId) {
      loadAttendance();
    }
  }, [selectedChildId]);

  const loadChildren = async () => {
    if (!parent?.id) return;

    try {
      // 백엔드 API를 통해 자녀 목록 조회 (RLS 우회)
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/parent/children`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '자녀 목록 조회 실패');
      }

      const data = await response.json();

      // 자녀 데이터 변환
      const childrenData: Child[] = data.map((child: any) => ({
        id: child.id,
        name: child.name || '이름 없음',
        organizationName: child.organization_name || '독서실',
      }));

      setChildren(childrenData);

      // 첫 번째 자녀 자동 선택
      if (childrenData.length > 0 && !selectedChildId) {
        setSelectedChildId(childrenData[0].id);
      }
    } catch (err: any) {
      console.error('자녀 목록 로드 오류:', err);
      setError(err.message || '자녀 목록을 불러오는데 실패했습니다.');
    }
  };

  const loadAttendance = async () => {
    if (!selectedChildId) return;

    setIsLoading(true);
    setError('');

    try {
      // 백엔드 API를 통해 출석 기록 조회 (RLS 우회)
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        setIsLoading(false);
        return;
      }

      // 이번 달 시작일/종료일 포맷
      const startDateStr = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      const endDateStr = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

      const response = await fetch(
        `${API_BASE_URL}/api/v1/parent/children/${selectedChildId}/attendance?start_date=${startDateStr}&end_date=${endDateStr}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '출석 기록 조회 실패');
      }

      const data = await response.json();
      const records = data.records || [];

      // 날짜별 그룹핑 (백엔드 응답 형식에 맞게 변환)
      const recordsByDate: { [key: string]: DailyRecord } = {};

      records.forEach((record: any) => {
        const date = record.date;

        if (!recordsByDate[date]) {
          recordsByDate[date] = {
            date: date,
            logs: [],
            hasOuting: false,
            status: record.status,
            expectedCheckin: record.expected_checkin,
            expectedCheckout: record.expected_checkout,
          };
        }

        // 체크인 시간이 있으면 입실 로그 추가
        if (record.checkin_time) {
          recordsByDate[date].logs.push({
            id: `${date}-entry`,
            timestamp: `${date}T${record.checkin_time}`,
            action: 'entry',
          });
          recordsByDate[date].firstEntry = `${date}T${record.checkin_time}`;
        }

        // 체크아웃 시간이 있으면 퇴실 로그 추가
        if (record.checkout_time) {
          recordsByDate[date].logs.push({
            id: `${date}-exit`,
            timestamp: `${date}T${record.checkout_time}`,
            action: 'exit',
          });
          recordsByDate[date].lastExit = `${date}T${record.checkout_time}`;
        }
      });

      setDailyRecords(Object.values(recordsByDate).sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      ));
    } catch (err: any) {
      console.error('출석 조회 오류:', err);
      setError(err.message || '출석 현황을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChildChange = (event: any) => {
    const newChildId = event.target.value as string;
    setSelectedChildId(newChildId);
    setDisplayCount(INITIAL_DISPLAY_COUNT); // 자녀 변경 시 표시 개수 리셋
    navigate(`/parent/attendance/${newChildId}`);
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + INITIAL_DISPLAY_COUNT);
  };

  const toggleDateExpand = async (date: string) => {
    const isCurrentlyExpanded = expandedDates.has(date);

    if (isCurrentlyExpanded) {
      // 닫기
      setExpandedDates(prev => {
        const newSet = new Set(prev);
        newSet.delete(date);
        return newSet;
      });
    } else {
      // 열기 - 상세 로그 로드
      setExpandedDates(prev => {
        const newSet = new Set(prev);
        newSet.add(date);
        return newSet;
      });

      // 해당 날짜의 상세 출입 로그 로드
      await loadDailyLogs(date);
    }
  };

  const loadDailyLogs = async (date: string) => {
    if (!selectedChildId) return;

    // 로딩 상태 설정
    setDailyRecords(prev => prev.map(record =>
      record.date === date ? { ...record, isLoading: true } : record
    ));

    try {
      const accessToken = await getAccessToken();
      

      if (!accessToken) return;

      const response = await fetch(
        `${API_BASE_URL}/api/v1/parent/children/${selectedChildId}/attendance/logs/${date}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        console.error('상세 로그 조회 실패');
        return;
      }

      const data = await response.json();
      const logs: AccessLog[] = data.logs || [];

      // 해당 날짜의 로그 업데이트
      setDailyRecords(prev => prev.map(record => {
        if (record.date === date) {
          const hasOuting = logs.some(log => log.action === 'outing');
          const firstEntryLog = logs.find(log => log.action === 'entry');
          const lastExitLog = [...logs].reverse().find(log => log.action === 'exit');

          return {
            ...record,
            logs: logs.map((log, idx) => ({
              id: log.id || `${date}-${idx}`,
              timestamp: log.timestamp,
              action: log.action,
              outing_type: log.outing_type,
            })),
            hasOuting,
            firstEntry: firstEntryLog?.timestamp,
            lastExit: lastExitLog?.timestamp,
            isLoading: false,
          };
        }
        return record;
      }));

    } catch (err) {
      console.error('상세 로그 로드 오류:', err);
      setDailyRecords(prev => prev.map(record =>
        record.date === date ? { ...record, isLoading: false } : record
      ));
    }
  };

  const getStatusChip = (record: DailyRecord) => {
    // 백엔드에서 계산한 status 사용
    const status = record.status;

    switch (status) {
      case '정상':
        return <Chip icon={<CheckCircleIcon />} label="정상" color="success" size="small" />;
      case '지각':
        return <Chip icon={<AccessTimeIcon />} label="지각" color="warning" size="small" />;
      case '조퇴':
        return <Chip icon={<LogoutIcon />} label="조퇴" color="warning" size="small" />;
      case '지각/조퇴':
        return <Chip icon={<AccessTimeIcon />} label="지각/조퇴" color="error" size="small" />;
      case '결석':
        return <Chip icon={<CancelIcon />} label="결석" color="error" size="small" />;
      case '휴무':
        return <Chip label="휴무" color="default" size="small" />;
      default:
        // status가 없으면 기존 로직 사용
        const hasExit = record.logs.some(log => log.action === 'exit');
        if (record.firstEntry && hasExit) {
          return <Chip icon={<CheckCircleIcon />} label="출석" color="success" size="small" />;
        } else if (record.firstEntry) {
          return <Chip icon={<AccessTimeIcon />} label="입실 중" color="primary" size="small" />;
        } else {
          return <Chip icon={<CancelIcon />} label="기록 없음" color="default" size="small" />;
        }
    }
  };

  const getActionLabel = (action: string, outing_type?: string, isFirstEntry?: boolean) => {
    if (action === 'entry') {
      return isFirstEntry ? '입실' : '복귀';
    } else if (action === 'exit') {
      return '퇴실';
    } else if (action === 'outing') {
      const outingLabels: { [key: string]: string } = {
        meal: '외출 (식사)',
        rest: '외출 (휴식)',
        hospital: '외출 (병원)',
        academy: '외출 (학원)',
        other: '외출 (기타)',
      };
      return outingLabels[outing_type || ''] || '외출';
    }
    return action;
  };

  const getActionIcon = (action: string) => {
    if (action === 'entry') {
      return <LoginIcon sx={{ fontSize: 16, color: 'success.main' }} />;
    } else if (action === 'exit') {
      return <LogoutIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    } else if (action === 'outing') {
      return <DirectionsWalkIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
    }
    return null;
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return new Date(timeString).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return `${date.getMonth() + 1}/${date.getDate()} (${days[date.getDay()]})`;
  };

  if (children.length === 0 && !isLoading) {
    return (
      <Box>
        <Alert severity="info">
          등록된 자녀가 없습니다. 독서실에 문의하여 자녀를 연결해주세요.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        출석 현황
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        이번 달 출석 기록을 확인할 수 있습니다.
      </Typography>

      {/* 자녀 선택 */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth sx={{ maxWidth: 300 }}>
          <InputLabel>자녀 선택</InputLabel>
          <Select
            value={selectedChildId}
            label="자녀 선택"
            onChange={handleChildChange}
          >
            {children.map((child) => (
              <MenuItem key={child.id} value={child.id}>
                {child.name} - {child.organizationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : dailyRecords.length === 0 ? (
        <Alert severity="info">
          이번 달 출석 기록이 없습니다.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {dailyRecords.slice(0, displayCount).map((record) => {
            const isExpanded = expandedDates.has(record.date);

            return (
              <Paper key={record.date} sx={{ overflow: 'hidden' }}>
                {/* 날짜 헤더 - 항상 클릭 가능 */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => toggleDateExpand(record.date)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(record.date)}
                    </Typography>
                    {getStatusChip(record)}
                    {record.hasOuting && (
                      <Chip
                        icon={<DirectionsWalkIcon />}
                        label="외출"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatTime(record.firstEntry)} ~ {formatTime(record.lastExit)}
                    </Typography>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                {/* 타임라인 상세 */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    {record.isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : record.logs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        출입 기록이 없습니다.
                      </Typography>
                    ) : (
                    <Box
                      sx={{
                        borderLeft: '2px solid',
                        borderColor: 'divider',
                        ml: 1,
                        pl: 2,
                      }}
                    >
                      {record.logs.map((log, index) => {
                        const isFirstEntry = log.action === 'entry' &&
                          record.logs.findIndex(l => l.action === 'entry') === index;

                        return (
                          <Box
                            key={log.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              py: 0.5,
                              position: 'relative',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: -18,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: log.action === 'entry' ? 'success.main' :
                                        log.action === 'exit' ? 'error.main' : 'warning.main',
                              },
                            }}
                          >
                            {getActionIcon(log.action)}
                            <Typography variant="body2" fontWeight={500}>
                              {formatTime(log.timestamp)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getActionLabel(log.action, log.outing_type, isFirstEntry)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}

          {/* 더보기 버튼 */}
          {dailyRecords.length > displayCount && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleLoadMore}
                startIcon={<KeyboardArrowDownIcon />}
                sx={{ borderRadius: 2, px: 4 }}
              >
                이전 기록 더보기 ({dailyRecords.length - displayCount}개 남음)
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
